'use client';

import { useEffect, useState } from 'react';
import {
  X,
  Zap,
  Waves,
  Target,
  Mic2,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

interface PitchDialogComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onGetStarted?: () => void;
  isAuthenticated?: boolean;
  hasLists?: boolean;
}

export function PitchDialogComponent({
  isOpen,
  onClose,
  onGetStarted,
  isAuthenticated = false,
  hasLists = false
}: PitchDialogComponentProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Auto-advance steps
  useEffect(() => {
    if (!isAnimating || !isOpen) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAnimating, isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleGetStarted = () => {
    onClose();
    onGetStarted?.();
  };

  const steps = [
    {
      icon: Waves,
      number: '1',
      subtitle: 'Drowning in noise',
      title: 'The Problem',
      description: 'Financial news is overwhelming. Thousands of articles daily, but how many actually matter to your investments?',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20'
    },
    {
      icon: Target,
      number: '2',
      subtitle: 'Precision filtering',
      title: 'Our Solution',
      description: 'TradedNews analyzes your portfolio and surfaces only the news that directly impacts your holdings.',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20'
    },
    {
      icon: Mic2,
      number: '3',
      subtitle: 'Listen anywhere',
      title: 'The Experience',
      description: 'Our AI transforms your daily briefing into professionally narrated audio. Your market intelligence, your way.',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20'
    }
  ];

  const painPoints = [
    'Generic market updates',
    'Hours of reading',
    'Information overload',
    'Irrelevant news'
  ];

  const benefits = [
    'Personalized to your portfolio',
    'AI-curated insights',
    'Audio briefings on-the-go',
    'Only what matters'
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto rounded-2xl bg-[#0d1117] border border-slate-800 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Background Effects */}
          <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
            <div className="absolute inset-0 opacity-30">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px),
                                   linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)`,
                  backgroundSize: '40px 40px'
                }}
              />
            </div>
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-amber-600/15 rounded-full blur-3xl" />
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 md:p-8">
            {/* Header Badge */}
            <div className="flex items-center gap-2 mb-6">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-amber-400 tracking-wider uppercase">
                Introducing TradedNews
              </span>
            </div>

            {/* Hero */}
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
              Your Portfolio.{' '}
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                Your News.
              </span>
              <br />
              Your Way.
            </h1>
            <p className="text-sm md:text-base text-slate-400 leading-relaxed mb-8">
              Stop drowning in generic market updates. Get personalized intelligence
              delivered as professionally narrated audio content.
            </p>

            {/* Before/After Comparison */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {/* Without */}
              <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1 rounded bg-red-500/20">
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </div>
                  <span className="text-sm font-semibold text-red-400">Without TradedNews</span>
                </div>
                <ul className="space-y-2">
                  {painPoints.map((point, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="w-1 h-1 rounded-full bg-slate-600" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              {/* With */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/20">
                <div className="flex items-center gap-2 mb-4">
                  <div className="p-1 rounded bg-emerald-500/20">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-semibold text-emerald-400">With TradedNews</span>
                </div>
                <ul className="space-y-2">
                  {benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How It Works */}
            <p className="text-center text-xs font-medium text-slate-500 uppercase tracking-widest mb-4">
              How It Works
            </p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = activeStep === index;
                return (
                  <button
                    key={index}
                    className={`relative p-4 rounded-xl text-left transition-all duration-300 ${
                      isActive
                        ? `${step.bgColor} ${step.borderColor} border`
                        : 'bg-slate-900/50 border border-slate-800 hover:border-slate-700'
                    }`}
                    onClick={() => {
                      setActiveStep(index);
                      setIsAnimating(false);
                    }}
                  >
                    {/* Number Badge */}
                    <div className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isActive ? step.bgColor + ' ' + step.color : 'bg-slate-800 text-slate-500'
                    } border ${isActive ? step.borderColor : 'border-slate-700'}`}>
                      {step.number}
                    </div>

                    <div className={`inline-flex p-2 rounded-lg mb-3 ${step.bgColor}`}>
                      <Icon className={`w-4 h-4 ${step.color}`} />
                    </div>
                    <div className={`text-[10px] font-medium uppercase tracking-wide mb-1 ${
                      isActive ? step.color : 'text-slate-600'
                    }`}>
                      {step.subtitle}
                    </div>
                    <div className="text-sm font-semibold text-white mb-2">
                      {step.title}
                    </div>
                    <p className={`text-[11px] leading-relaxed ${
                      isActive ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {step.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Progress Dots */}
            <div className="flex justify-center gap-1.5 mb-8">
              {steps.map((step, index) => (
                <button
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    activeStep === index
                      ? `w-6 ${activeStep === 0 ? 'bg-red-400' : activeStep === 1 ? 'bg-blue-400' : 'bg-amber-400'}`
                      : 'w-1.5 bg-slate-700 hover:bg-slate-600'
                  }`}
                  onClick={() => {
                    setActiveStep(index);
                    setIsAnimating(false);
                  }}
                />
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-slate-800">
              <div>
                {!isAuthenticated ? (
                  <>
                    <h3 className="text-lg font-bold text-white">Ready to reclaim your time?</h3>
                    <p className="text-xs text-slate-500">
                      Join thousands of investors getting smarter market intelligence.
                    </p>
                  </>
                ) : !hasLists ? (
                  <>
                    <h3 className="text-lg font-bold text-white">Create your first list</h3>
                    <p className="text-xs text-slate-500">
                      Start tracking the stocks that matter to you.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-bold text-white">Ready to reclaim your time?</h3>
                    <p className="text-xs text-slate-500">
                      Get personalized intelligence for your portfolio.
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={handleGetStarted}
                className="group flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-semibold text-sm shadow-lg shadow-orange-500/20 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/30 hover:brightness-110 whitespace-nowrap"
              >
                {!isAuthenticated ? 'Sign In' : !hasLists ? 'Create List' : 'Start Free Trial'}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
